'use client'

import { CouponCallout, type CouponCalloutText } from '@/components/client/shared/coupon-callout'
import { useTranslation } from '@/hooks/client/use-translation'
import { formatPrice } from '@/lib/utils/format-price'

import type { PriceSellerData } from './price-section-types'

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 512 512" fill="white" className="ml-1.5" aria-hidden="true">
    <path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l82.7 0-201.4 201.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3 448 192c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160c0-17.7-14.3-32-32-32L320 0zM80 96C35.8 96 0 131.8 0 176L0 432c0 44.2 35.8 80 80 80l256 0c44.2 0 80-35.8 80-80l0-80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 80c0 8.8-7.2 16-16 16L80 448c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l80 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 96z" />
  </svg>
)

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 512 512" fill="#179F2D" aria-hidden="true" className="mt-0.5 shrink-0">
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
  </svg>
)

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-3.5 w-3.5 shrink-0">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
    />
    <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
  </svg>
)

const StarIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 23 22" fill="none" aria-hidden="true" className="shrink-0">
    <path
      d="M11.164 16.549l4.851-1.23 2.027 6.247-6.878-5.017zm11.164-8.074h-8.539l-2.625-8.04-2.625 8.04H0l6.911 4.984-2.625 8.04 6.911-4.983 4.253-3.057 6.878-4.984z"
      fill="#219653"
    />
  </svg>
)

interface SellerCardProps {
  seller: PriceSellerData
  isPrimary: boolean
  releaseSlug: string
  couponText: CouponCalloutText
}

/** The stacked seller card of the pricing section's control arm. */
export const SellerCard = ({ seller, isPrimary, releaseSlug, couponText }: SellerCardProps) => {
  const { t } = useTranslation()
  const hasDiscount = seller.discount != null && seller.discount > 0 && seller.discountPrice != null
  const savings = hasDiscount ? seller.price - (seller.discountPrice ?? 0) : 0

  return (
    <div
      className={`group relative rounded-2xl border bg-white transition-shadow ${
        isPrimary ? 'border-lapis/20 ring-lapis/[0.08] shadow-md ring-1' : 'border-slate-200 shadow-sm hover:shadow-md'
      }`}
    >
      {isPrimary && (
        <div className="bg-lapis absolute -top-3 left-5 rounded-full px-3 py-0.5 text-xs font-bold tracking-wide text-white uppercase">
          {t('product.featured', 'Featured')}
        </div>
      )}

      <div className="flex flex-col min-[730px]:flex-row">
        {/* Left: seller info */}
        <div className="min-w-0 p-5 min-[730px]:flex-1 min-[730px]:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3.5">
              {seller.logoUrl && (
                <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-100 bg-white p-1">
                  <img
                    src={seller.logoUrl}
                    alt={`${seller.sellerName} logo`}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    className="max-h-10 w-auto object-contain"
                  />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="text-navy text-lg leading-tight font-bold">{seller.sellerName}</h3>
                {seller.sellerTagline && <p className="text-navy-50 mt-0.5 text-sm">{seller.sellerTagline}</p>}
                {seller.sellerRating != null && seller.sellerRating > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <StarIcon size={14} />
                    <span className="text-navy text-xs leading-none font-bold">{seller.sellerRating}</span>
                    {seller.sellerReviews != null && seller.sellerReviews > 0 && (
                      <span className="text-navy-40 text-xs">({seller.sellerReviews.toLocaleString('en-US')})</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {seller.offerTagline && (
            <div className="mt-3">
              <span className="bg-lapis/8 text-lapis inline-flex self-start rounded-full px-2.5 py-1 text-xs font-semibold">
                {seller.offerTagline}
              </span>
            </div>
          )}

          {seller.sellerBlurb && (
            <p className="text-navy-70 mt-4 text-[14px] leading-relaxed min-[730px]:text-[15px]">
              {seller.sellerBlurb}
            </p>
          )}

          {seller.sellerFeatures.length > 0 && (
            <ul className="mt-4 grid grid-cols-1 gap-x-4 gap-y-1.5 border-t border-slate-100 pt-3 min-[730px]:grid-cols-2">
              {seller.sellerFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <CheckIcon />
                  <span className="text-navy-80 text-[14px] leading-snug min-[730px]:text-[15px]">{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: price + CTA */}
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-4 min-[730px]:w-[250px] min-[730px]:shrink-0 min-[730px]:flex-col min-[730px]:items-center min-[730px]:justify-center min-[730px]:gap-2.5 min-[730px]:border-t-0 min-[730px]:border-l min-[730px]:border-slate-100 min-[730px]:px-6 min-[730px]:py-6">
          <div className="flex flex-col items-start min-[730px]:items-center">
            {hasDiscount && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400 line-through">{formatPrice(seller.price)}</span>
                <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
                  -{formatPrice(savings)}
                </span>
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-navy text-2xl leading-tight font-extrabold tracking-tight">
                {hasDiscount && seller.discountPrice != null
                  ? formatPrice(seller.discountPrice)
                  : formatPrice(seller.price)}
              </span>
            </div>
            <span className="text-navy-40 text-xs">
              {seller.priceType && /monthly/.test(seller.priceType)
                ? t('price.perPairPerMonth', '/ pair / mth')
                : t('price.perPair', '/ pair')}
            </span>
          </div>

          {(seller.url || (seller.coupon && seller.couponLabel)) && (
            <div className="flex flex-col items-center gap-2 min-[730px]:w-full">
              {seller.url && (
                <a
                  href={seller.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="bg-lapis hover:bg-navy flex h-[44px] w-full items-center justify-center rounded-xl px-6 text-sm font-bold text-white shadow-sm transition-all duration-150 hover:shadow-md active:scale-[0.97]"
                  data-track
                  data-track-impression
                  data-event-name="AffiliateAction"
                  data-event-seller={seller.sellerName}
                  data-affiliate-seller-type={seller.sellerType}
                  data-event-product={releaseSlug}
                  data-event-position="product-prices"
                  data-affiliate-cta={t('client.product.learnMore', 'Learn More')}
                  data-affiliate-text={t('client.product.learnMore', 'Learn More')}
                  data-affiliate-test-name="product-prices"
                  data-event-url={seller.url}
                >
                  {t('client.product.learnMore', 'Learn More')}
                  <ExternalLinkIcon />
                </a>
              )}

              {/* Coupon — directly under the Learn More button */}
              {seller.coupon && seller.couponLabel && (
                <CouponCallout
                  coupon={seller.coupon}
                  affiliateUrl={seller.url ?? undefined}
                  sellerName={seller.sellerName}
                  tracking={{
                    position: 'product-prices-coupon',
                    product: releaseSlug,
                    seller: seller.sellerName,
                    sellerType: seller.sellerType,
                  }}
                  label={seller.couponLabel}
                  text={couponText}
                >
                  <TagIcon />
                  {seller.couponLabel}
                </CouponCallout>
              )}
            </div>
          )}

          {seller.sellerReviews != null && seller.sellerReviews > 0 && (
            <div className="hidden items-center gap-1 lg:flex">
              <StarIcon size={12} />
              <span className="text-navy-40 text-[11px]">
                {seller.sellerReviews.toLocaleString('en-US')} {t('product.reviews', 'reviews')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
